import React, { useMemo, useState } from 'react';
import Box from '@mui/joy/Box';
import Input from '@mui/joy/Input';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemButton, { listItemButtonClasses } from '@mui/joy/ListItemButton';
import ListItemDecorator from '@mui/joy/ListItemDecorator';
import Typography from '@mui/joy/Typography';
import Avatar from '@mui/joy/Avatar';
import Badge from '@mui/joy/Badge';
import Chip from '@mui/joy/Chip';
import Menu from '@mui/joy/Menu';
import MenuItem from '@mui/joy/MenuItem';
import Dropdown from '@mui/joy/Dropdown';
import MenuButton from '@mui/joy/MenuButton';
import ListItemContent from '@mui/joy/ListItemContent';
import CircularProgress from '@mui/joy/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import CheckIcon from '@mui/icons-material/Check';
import { ClientType, CLIENT_TYPE_LABELS } from '../../types/client';

export interface Conversation {
  clientReference: string;
  clientName: string;
  phoneNumber: string;
  lastMessage: string | null;
  lastMessageTime: Date | null;
  unreadCount: number;
  messagesCredentialReference: string;
  clientType?: ClientType;
}

type ClientTypeFilter = 'all' | ClientType;

interface ConversationListProps {
  conversations: Conversation[];
  selectedClientReference: string | null;
  onSelectConversation: (clientReference: string) => void;
  isLoading: boolean;
}

const FILTER_OPTIONS: { value: ClientTypeFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: ClientType.CLIENT, label: CLIENT_TYPE_LABELS[ClientType.CLIENT] },
  { value: ClientType.SUPPLIER, label: CLIENT_TYPE_LABELS[ClientType.SUPPLIER] },
  { value: ClientType.PERSONAL, label: CLIENT_TYPE_LABELS[ClientType.PERSONAL] },
];

const TYPE_CHIP_COLOR: Record<ClientType, 'primary' | 'warning' | 'success'> = {
  [ClientType.CLIENT]: 'primary',
  [ClientType.SUPPLIER]: 'warning',
  [ClientType.PERSONAL]: 'success',
};

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  selectedClientReference,
  onSelectConversation,
  isLoading,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<ClientTypeFilter>('all');

  const filteredConversations = useMemo(() => {
    const lower = searchTerm.trim().toLowerCase();
    return conversations.filter((conv) => {
      const matchesSearch =
        !lower ||
        conv.clientName.toLowerCase().includes(lower) ||
        conv.phoneNumber.includes(searchTerm);
      const matchesType =
        typeFilter === 'all' || conv.clientType === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [conversations, searchTerm, typeFilter]);

  const formatTime = (date: Date | null): string => {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return d.toLocaleDateString('es-AR');
  };

  const formatLastMessage = (value: string | null): string | null => {
    if (!value) {
      return null;
    }
    const trimmed = value.trim();
    if (trimmed.startsWith('BEGIN:VCARD')) {
      return 'Contacto';
    }
    if (trimmed === '[reactionMessage]' || trimmed === '[contactMessage]') {
      return trimmed === '[reactionMessage]' ? 'Reacción' : 'Contacto';
    }
    return trimmed;
  };

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const activeFilterLabel =
    FILTER_OPTIONS.find((o) => o.value === typeFilter)?.label ?? 'Todos';
  const isFilterActive = typeFilter !== 'all';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography level="title-md" sx={{ mb: 1.5 }}>
          Mensajes
        </Typography>
        <Input
          size="sm"
          startDecorator={<SearchIcon sx={{ color: 'neutral.500' }} />}
          placeholder="Buscar cliente..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ '--Input-radius': '8px' }}
        />

        <Box sx={{ mt: 1.25, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Dropdown>
            <MenuButton
              size="sm"
              variant={isFilterActive ? 'soft' : 'outlined'}
              color={isFilterActive ? 'primary' : 'neutral'}
              startDecorator={<TuneIcon fontSize="small" />}
              sx={{
                borderRadius: '999px',
                fontWeight: 500,
                px: 1.5,
                minHeight: 32,
              }}
            >
              Filtros
              {isFilterActive && (
                <Chip
                  size="sm"
                  variant="solid"
                  color="primary"
                  sx={{ ml: 0.75, '--Chip-minHeight': '18px', fontSize: '0.7rem' }}
                >
                  1
                </Chip>
              )}
            </MenuButton>
            <Menu placement="bottom-start" sx={{ minWidth: 220, p: 1 }}>
              <Typography
                level="body-xs"
                sx={{ px: 1, py: 0.5, color: 'neutral.500', textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                Tipo de cliente
              </Typography>
              {FILTER_OPTIONS.map((opt) => {
                const selected = typeFilter === opt.value;
                return (
                  <MenuItem
                    key={opt.value}
                    selected={selected}
                    onClick={() => setTypeFilter(opt.value)}
                    sx={{ borderRadius: 'sm' }}
                  >
                    <ListItemDecorator>
                      {selected ? (
                        <CheckIcon fontSize="small" color="primary" />
                      ) : (
                        <Box sx={{ width: 20 }} />
                      )}
                    </ListItemDecorator>
                    <ListItemContent>{opt.label}</ListItemContent>
                  </MenuItem>
                );
              })}
            </Menu>
          </Dropdown>

          {isFilterActive && (
            <Chip
              size="sm"
              variant="soft"
              color="primary"
              onClick={() => setTypeFilter('all')}
              endDecorator={
                <Box component="span" sx={{ fontSize: '0.85rem', lineHeight: 1 }}>
                  ×
                </Box>
              }
              sx={{ cursor: 'pointer' }}
            >
              {activeFilterLabel}
            </Chip>
          )}
        </Box>
      </Box>

      <List
        sx={{
          flex: 1,
          overflow: 'auto',
          '--ListItem-paddingY': '8px',
          '--ListItem-paddingX': '8px',
          p: 1,
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
            }}
          >
            <CircularProgress size="sm" />
          </Box>
        ) : filteredConversations.length === 0 ? (
          <Typography level="body-sm" sx={{ color: 'neutral.500', textAlign: 'center', py: 3 }}>
            {searchTerm || isFilterActive
              ? 'No se encontraron conversaciones'
              : 'Sin conversaciones aún'}
          </Typography>
        ) : (
          filteredConversations.map((conversation) => {
            const lastMessagePreview = formatLastMessage(conversation.lastMessage);
            return (
              <ListItem
                key={`${conversation.clientReference}-${conversation.messagesCredentialReference}`}
              >
                <ListItemButton
                  selected={selectedClientReference === conversation.clientReference}
                  onClick={() => onSelectConversation(conversation.clientReference)}
                  sx={{
                    borderRadius: 'sm',
                    [`&.${listItemButtonClasses.selected}`]: {
                      bgcolor: 'primary.softBg',
                    },
                  }}
                >
                  <ListItemDecorator sx={{ alignSelf: 'flex-start' }}>
                    <Badge
                      badgeContent={
                        conversation.unreadCount > 0
                          ? conversation.unreadCount > 9
                            ? '9+'
                            : conversation.unreadCount
                          : 0
                      }
                      size="sm"
                      color="danger"
                      invisible={conversation.unreadCount === 0}
                    >
                      <Avatar size="sm" sx={{ bgcolor: 'primary.softBg', color: 'primary.softColor' }}>
                        {getInitials(conversation.clientName)}
                      </Avatar>
                    </Badge>
                  </ListItemDecorator>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                        <Typography level="title-sm" sx={{ fontWeight: 'lg' }} noWrap>
                          {conversation.clientName}
                        </Typography>
                        {conversation.clientType && (
                          <Chip
                            size="sm"
                            variant="soft"
                            color={TYPE_CHIP_COLOR[conversation.clientType]}
                            sx={{ '--Chip-minHeight': '18px', fontSize: '0.65rem', px: 0.5 }}
                          >
                            {CLIENT_TYPE_LABELS[conversation.clientType]}
                          </Chip>
                        )}
                      </Box>
                      <Typography level="body-xs" sx={{ color: 'neutral.500', flexShrink: 0 }}>
                        {formatTime(conversation.lastMessageTime)}
                      </Typography>
                    </Box>
                    <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                      {conversation.phoneNumber}
                    </Typography>
                    {lastMessagePreview ? (
                      <Typography level="body-sm" sx={{ color: 'neutral.600', mt: 0.25 }} noWrap>
                        {lastMessagePreview}
                      </Typography>
                    ) : null}
                  </Box>
                </ListItemButton>
              </ListItem>
            );
          })
        )}
      </List>
    </Box>
  );
};

export default ConversationList;
